import { Box, IconButton, Tooltip } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { msg } from '@lingui/macro';
import SvgCustomIcon from '../icons/SvgCustomIcon';
import {
  getBasicOrganizationIconName,
  hasMergeDate,
  isValidatedMasterRow,
} from './crash-list-merge-provenance.util';

export {
  getBasicOrganizationIconName,
  hasMergeDate,
  isValidatedMasterRow,
  splitCrashListRowsByValidation,
} from './crash-list-merge-provenance.util';

export function getBasicOrganizationLabel(basicOrganization, translate) {
  const basic = Number(basicOrganization);
  if (basic === 1) return translate(msg`Police`);
  if (basic === 2) return translate(msg`Healthcare`);
  if (basic === 3) return translate(msg`Insurance`);
  return '';
}

function buildSourceReportsColumn(translate) {
  return {
    field: 'source_reports',
    headerName: translate(msg`Source Reports`),
    headerClassName: 'overload-theme--header',
    width: 120,
    minWidth: 96,
    sortable: false,
    filterable: false,
    rowSpanValueGetter: (value, row) => (row ? row['Crash id'] : null),
    renderCell: (params) => {
      if (!isValidatedMasterRow(params.row)) {
        return null;
      }
      const basics = Array.isArray(params.row.merged_from_basics)
        ? params.row.merged_from_basics
        : [];
      if (basics.length === 0) {
        return null;
      }
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: '100%' }}>
          {basics.map((basic) => {
            const iconName = getBasicOrganizationIconName(basic);
            if (!iconName) return null;
            const sourceLabel = getBasicOrganizationLabel(basic, translate);
            return (
              <Tooltip key={basic} title={sourceLabel}>
                <Box sx={{ display: 'inline-flex' }} aria-label={sourceLabel}>
                  <SvgCustomIcon name={iconName} size={16} />
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      );
    },
  };
}

function buildMergedToColumn(translate) {
  return {
    field: 'merged_to',
    headerName: translate(msg`Merged to`),
    headerClassName: 'overload-theme--header',
    width: 168,
    minWidth: 140,
    flex: 0.6,
    sortable: false,
    filterable: false,
    cellClassName: 'merged-to-cell',
    rowSpanValueGetter: (value, row) => (row ? row['Crash id'] : null),
    renderCell: (params) => {
      if (isValidatedMasterRow(params.row)) {
        return null;
      }
      const destCrashId =
        String(params.row.merged_to_crash_id || params.row.match_id || '').trim();
      if (!destCrashId) {
        return null;
      }
      const destBasic = params.row.merged_to_basic;
      const iconName = getBasicOrganizationIconName(destBasic);
      const destOrgLabel = getBasicOrganizationLabel(destBasic, translate);
      const tooltip = destOrgLabel
        ? `${destOrgLabel} · ${destCrashId}`
        : destCrashId;
      return (
        <Tooltip title={tooltip}>
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              lineHeight: 'inherit',
              whiteSpace: 'nowrap',
            }}
            aria-label={tooltip}
          >
            {iconName ? <SvgCustomIcon name={iconName} size={16} /> : null}
            <Box component="span" sx={{ lineHeight: 'inherit' }}>
              {destCrashId}
            </Box>
          </Box>
        </Tooltip>
      );
    },
  };
}

function buildOrganizationColumn(base, translate) {
  return {
    ...base,
    filterable: false,
    minWidth: 100,
    valueGetter: (value, row) => {
      if (!row?.merge_date) return value;
      return isValidatedMasterRow(row) ? translate(msg`Merged`) : value;
    },
  };
}

function buildValidatedLockColumn({ translate, isAdmin, onLockClick, showValidatedLocks }) {
  return {
    field: 'validated_lock',
    headerName: '',
    width: 40,
    minWidth: 40,
    sortable: false,
    filterable: false,
    headerClassName: 'overload-theme--header',
    rowSpanValueGetter: (value, row) => (row ? row['Crash id'] : null),
    renderCell: (params) => {
      if (showValidatedLocks && hasMergeDate(params.row)) {
        const isMaster = isValidatedMasterRow(params.row);
        const lockTitle = isAdmin && isMaster
          ? translate(msg`Modify validated crash`)
          : translate(msg`Already validated`);
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
            {isAdmin && isMaster ? (
              <Tooltip title={lockTitle}>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    onLockClick(params.row);
                  }}
                  aria-label={lockTitle}
                >
                  <LockIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title={lockTitle}>
                <Box sx={{ display: 'flex', alignItems: 'center', color: 'action.active' }} aria-label={lockTitle}>
                  <LockIcon fontSize="small" />
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      }
      if (!showValidatedLocks && params.row.organizationValue === 2 && params.row.is_closed !== undefined) {
        const isClosed = params.row.is_closed === true;
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                backgroundColor: isClosed ? 'success.main' : 'error.main',
              }}
              title={isClosed ? translate(msg`Closed`) : translate(msg`Open`)}
            />
          </Box>
        );
      }
      return null;
    },
  };
}

function mapModelColumns(modelColumns, options) {
  const {
    sortableFields,
    dateFilterOperators,
    stringFilterOperators,
    translate,
    mergeColumnMode,
  } = options;
  return modelColumns.flatMap((col) => {
    const isDateCol = /\bdate\b/i.test(col.field);
    const base = {
      ...col,
      sortable: sortableFields.has(col.field),
    };
    if (col.field !== 'organization') {
      return [{
        ...base,
        filterOperators: isDateCol ? dateFilterOperators : stringFilterOperators,
      }];
    }
    const organizationCol = buildOrganizationColumn(base, translate);
    if (mergeColumnMode === 'none') {
      return [organizationCol];
    }
    if (mergeColumnMode === 'merged-to') {
      return [organizationCol, buildMergedToColumn(translate)];
    }
    return [
      organizationCol,
      buildSourceReportsColumn(translate),
      buildMergedToColumn(translate),
    ];
  });
}

function buildColumnGroupingWithMergeCols(columnGroupingModel) {
  return (columnGroupingModel || []).map((group) => {
    if (group.groupId !== 'crash') return group;
    const children = [...(group.children || [])];
    if (children.findIndex((child) => child.field === 'organization') < 0) return group;
    const withoutMerge = children.filter(
      (child) => child.field !== 'source_reports' && child.field !== 'merged_to',
    );
    const orgPos = withoutMerge.findIndex((child) => child.field === 'organization');
    withoutMerge.splice(orgPos + 1, 0, { field: 'source_reports' }, { field: 'merged_to' });
    return { ...group, children: withoutMerge };
  });
}

export function buildCrashListGridColumns(options) {
  const {
    configuration,
    translate,
    sortableFields,
    dateFilterOperators,
    stringFilterOperators,
    isAdmin,
    onLockClick,
    mergeColumnMode = 'none',
    showValidatedLocks,
    getColumnsModel,
  } = options;
  const translateColumn = (label) => {
    if (label === 'Report Type') return translate(msg`Report Type`);
    if (label === 'Merged') return translate(msg`Merged`);
    if (label === 'Source Reports') return translate(msg`Source Reports`);
    if (label === 'Merged to') return translate(msg`Merged to`);
    return label;
  };
  const { columns: modelColumns, columnGroupingModelWOrg } = getColumnsModel(
    configuration,
    translate,
    translateColumn,
  );
  const dataColumns = mapModelColumns(modelColumns, {
    sortableFields,
    dateFilterOperators,
    stringFilterOperators,
    translate,
    mergeColumnMode,
  });
  const lockColumn = buildValidatedLockColumn({
    translate,
    isAdmin,
    onLockClick,
    showValidatedLocks,
  });
  return {
    columns: [lockColumn, ...dataColumns],
    columnGroupingModel: mergeColumnMode === 'both'
      ? buildColumnGroupingWithMergeCols(columnGroupingModelWOrg)
      : columnGroupingModelWOrg,
  };
}
